import { Fragment, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DealerService } from '../../services';
import { useNotification } from '../../contexts/NotificationContext';
import { useError } from '../../contexts/ErrorContext';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import SendIcon from '@material-ui/icons/Send';
import CreateIcon from '@material-ui/icons/Create';
import PublishIcon from '@material-ui/icons/Publish';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';
import ConfirmationModal from '../../components/ConfirmationModal';
import { isSuccessfulResponse } from '../../utils/responseHelpers';

const MAX_BASIS_POINTS = 10000;

const styles = makeStyles(() => ({
    dealerBpsSchedules: {
        maxHeight: '60vh',
        overflowY: 'scroll'
    },
    input: {
        marginLeft: '25px',
        marginTop: '15px',
        width: '120px'
    },
    submitButton: {
        marginLeft: '10px',
        marginTop: '25px'
    },
    editInput: {
        width: '90px'
    },
    emptyFooter: {
        fontSize: '12px',
        textAlign: 'center'
    }
}));

// `days` is a lower-bound threshold: the tier that applies to an elapsed day count is the one
// with the largest `days` that is <= that count.
const isWholeNumberBetween = (value, min, max) => {
    if (value === null || value === undefined || String(value).trim() === '') return false;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= min && parsed <= max;
};

const formatRate = (basisPoints) => `${(basisPoints / 100).toFixed(2)}%`;

const DealerBpsSchedules = ({ dealerNumber }) => {
    const classes = styles();
    const [schedules, setSchedules] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [daysInput, setDaysInput] = useState('');
    const [bpsInput, setBpsInput] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editDays, setEditDays] = useState('');
    const [editBps, setEditBps] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const { createErrorNotification } = useNotification();
    const { showError } = useError();

    const loadSchedules = () => {
        setLoadError(false);
        return DealerService.getBpsSchedules(dealerNumber)
            .then(setSchedules)
            .catch(err => {
                setLoadError(true);
                showError(err, `Failed to load BPS schedules for dealer: ${dealerNumber}`);
            });
    };

    useEffect(() => {
        loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealerNumber]);

    const notifyInvalidTier = () => createErrorNotification(
        `Enter a whole number of days (0 or more) and basis points between 0 and ${MAX_BASIS_POINTS}.`
    );

    const isValidTier = (days, basisPoints) =>
        isWholeNumberBetween(days, 0, Number.MAX_SAFE_INTEGER) &&
        isWholeNumberBetween(basisPoints, 0, MAX_BASIS_POINTS);

    const handleCreate = async () => {
        if (!isValidTier(daysInput, bpsInput)) return notifyInvalidTier();

        try {
            const response = await DealerService.newBpsSchedule(dealerNumber, {
                days: Number(daysInput),
                basisPoints: Number(bpsInput)
            });

            if (isSuccessfulResponse(response)) {
                setDaysInput('');
                setBpsInput('');
                await loadSchedules();
            } else {
                createErrorNotification('Failed to create BPS schedule, please contact support');
            }
        } catch (err) {
            showError(err, err?.normalizedMessage);
        }
    };

    const startEditing = (row) => {
        setEditingId(row.id);
        setEditDays(String(row.days));
        setEditBps(String(row.basisPoints));
    };

    const cancelEditing = () => setEditingId(null);

    const handleUpdate = async () => {
        if (!isValidTier(editDays, editBps)) return notifyInvalidTier();

        try {
            const response = await DealerService.updateBpsSchedule(dealerNumber, editingId, {
                days: Number(editDays),
                basisPoints: Number(editBps)
            });

            if (isSuccessfulResponse(response)) {
                setEditingId(null);
                await loadSchedules();
            } else {
                createErrorNotification('Failed to update BPS schedule, please contact support');
            }
        } catch (err) {
            showError(err, err?.normalizedMessage);
        }
    };

    const handleDeleteClick = (id) => {
        setPendingDeleteId(id);
        setShowDeleteConfirmation(true);
    };

    const cancelDelete = () => {
        setPendingDeleteId(null);
        setShowDeleteConfirmation(false);
    };

    const handleDelete = async () => {
        try {
            const response = await DealerService.deleteBpsSchedule(dealerNumber, pendingDeleteId);

            if (isSuccessfulResponse(response)) await loadSchedules();
            else createErrorNotification('Failed to delete BPS schedule, please contact support');
        } catch (err) {
            showError(err, err?.normalizedMessage);
        } finally {
            cancelDelete();
        }
    };

    if (schedules === null && loadError) {
        return (
            <div>
                <p>Failed to load BPS schedules.</p>
                <Button variant="contained" color="primary" onClick={loadSchedules}>Retry</Button>
            </div>
        );
    }

    if (schedules === null) return (<h1>Loading...</h1>);

    return (
        <div className={classes.dealerBpsSchedules}>
            <ConfirmationModal
                title="Confirm"
                message="Are you sure you want to delete this BPS schedule tier?"
                onConfirm={handleDelete}
                onCancel={cancelDelete}
                showConfirmationBox={showDeleteConfirmation}
            />
            <Grid item>
                <div>
                    <TextField
                        className={classes.input}
                        type="number"
                        label="Days"
                        placeholder="30"
                        value={daysInput}
                        onChange={(event) => setDaysInput(event.target.value)}
                        variant="outlined"
                    />
                    <TextField
                        className={classes.input}
                        type="number"
                        label="Basis Points"
                        placeholder="250"
                        value={bpsInput}
                        onChange={(event) => setBpsInput(event.target.value)}
                        variant="outlined"
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.submitButton}
                        endIcon={<SendIcon/>}
                        onClick={handleCreate}
                    >Add
                    </Button>
                </div>
                {schedules.length === 0 ?
                    <h3 className={classes.emptyFooter}>No BPS Schedules</h3> :
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left"><b>Days (&ge;)</b></TableCell>
                                    <TableCell align="left"><b>Basis Points</b></TableCell>
                                    <TableCell align="left"><b>Rate</b></TableCell>
                                    <TableCell align="left"><b>Action</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {schedules.map((row) => (
                                    <TableRow key={row.id}>
                                        {editingId === row.id ?
                                            <Fragment>
                                                <TableCell>
                                                    <TextField
                                                        className={classes.editInput}
                                                        type="number"
                                                        value={editDays}
                                                        onChange={(event) => setEditDays(event.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        className={classes.editInput}
                                                        type="number"
                                                        value={editBps}
                                                        onChange={(event) => setEditBps(event.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatRate(Number(editBps) || 0)}</TableCell>
                                                <TableCell>
                                                    <IconButton onClick={handleUpdate}><PublishIcon/></IconButton>
                                                    <IconButton onClick={cancelEditing}><CloseIcon/></IconButton>
                                                </TableCell>
                                            </Fragment> :
                                            <Fragment>
                                                <TableCell>{row.days}</TableCell>
                                                <TableCell>{row.basisPoints}</TableCell>
                                                <TableCell>{formatRate(row.basisPoints)}</TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => startEditing(row)}><CreateIcon/></IconButton>
                                                    <IconButton onClick={() => handleDeleteClick(row.id)}>
                                                        <DeleteOutlineTwoToneIcon/>
                                                    </IconButton>
                                                </TableCell>
                                            </Fragment>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>}
            </Grid>
        </div>
    );
};

export default DealerBpsSchedules;
